import { useState, useEffect } from 'react'
import { jsPDF } from 'jspdf'
import './FormContainer.css'
import { translations } from '../../data/translations'
import ContactSection from '../FormSections/ContactSection'
import ProjectTypeSection from '../FormSections/ProjectTypeSection'
import OrganizationSection from '../FormSections/OrganizationSection'
import ContentSection from '../FormSections/ContentSection'
import DesignSection from '../FormSections/DesignSection'
import FeaturesSection from '../FormSections/FeaturesSection'
import TechSection from '../FormSections/TechSection'
import ShopSection from '../FormSections/ShopSection'
import ComplianceSection from '../FormSections/ComplianceSection'
import SEOSection from '../FormSections/SEOSection'
import PerformanceSection from '../FormSections/PerformanceSection'
import AccessibilitySection from '../FormSections/AccessibilitySection'
import MigrationSection from '../FormSections/MigrationSection'
import SupportSection from '../FormSections/SupportSection'
import TimelineBudgetSection from '../FormSections/TimelineBudgetSection'
import PrioritiesSection from '../FormSections/PrioritiesSection'
import StakeholdersSection from '../FormSections/StakeholdersSection'
import AdditionalSection from '../FormSections/AdditionalSection'
import FilesSection from '../FormSections/FilesSection'

const FormContainer = ({ language, onProgressChange }) => {
  const [formData, setFormData] = useState({})
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [hasDownloadedPdf, setHasDownloadedPdf] = useState(false)

  const t = translations[language] || translations.de
  const isFrench = language === 'fr'
  const introCopy = isFrench
    ? {
        title: 'Bienvenue dans le questionnaire de planification website Enterprise!',
        text:
          "Ce questionnaire nous aide a planifier, estimer et realiser un site web professionnel pour votre association. Merci de repondre a toutes les questions aussi completement que possible. Les champs marques d'un * sont obligatoires.",
      }
    : {
        title: 'Willkommen zum Enterprise Website-Planungs-Fragebogen!',
        text:
          'Dieser Fragebogen hilft uns, eine professionelle Website fuer Ihren Verein zu planen, zu kalkulieren und umzusetzen. Bitte beantworten Sie alle Fragen so vollstaendig wie moeglich. Mit einem * markierte Felder sind Pflichtfelder.',
      }

  // Calculate progress based on filled fields
  useEffect(() => {
    const requiredFields = [
      'ansprechpartner',
      'email',
      'projekt_typ',
      'projekt_beschreibung',
      'cms_benoetigt',
      'shop_benoetigt',
      'compliance',
      'sensible_daten',
      'hosting_status',
      'traffic',
      'barrierefreiheit_niveau',
      'launch_zeitraum'
    ]
    
    const totalFields = 150 // Approximate total meaningful fields (18 sections)
    const filledFields = Object.entries(formData).filter(([key, value]) => {
      if (!value) return false
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'string') return value.trim() !== ''
      return true
    }).length
    
    const requiredFilled = requiredFields.filter(field => {
      const value = formData[field]
      if (Array.isArray(value)) return value.length > 0
      return value && value.toString().trim() !== ''
    }).length
    
    // Weight required fields more heavily
    const requiredProgress = (requiredFilled / requiredFields.length) * 40
    const optionalProgress = (filledFields / totalFields) * 60
    const progress = Math.min(requiredProgress + optionalProgress, 100)
    
    onProgressChange(progress)
  }, [formData, onProgressChange])

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const formatKeyAsLabel = (key) => {
    return String(key)
      .replace(/\[\]$/g, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (ch) => ch.toUpperCase())
  }

  const formatValueForPdf = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) => (item instanceof File ? item.name : String(item)))
        .filter(Boolean)
        .join(', ')
    }

    if (value instanceof File) {
      return value.name
    }

    if (typeof value === 'string') {
      return value.trim()
    }

    return value === undefined || value === null ? '' : String(value)
  }

  const handleDownloadPdf = () => {
    const filledEntries = Object.entries(formData)
      .map(([key, value]) => [formatKeyAsLabel(key), formatValueForPdf(value)])
      .filter(([, value]) => value !== '')

    if (!filledEntries.length) {
      alert(t.downloadEmptyWarning || 'Bitte fuellen Sie zuerst mindestens ein Feld aus.')
      return
    }

    setIsGeneratingPdf(true)
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const marginX = 48
      const maxTextWidth = pageWidth - marginX * 2
      let y = 56

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(17)
      doc.text(t.pdfTitle || 'Website Anfrage Formular', marginX, y)

      y += 22
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text(
        `${t.pdfGeneratedOn || 'Erstellt am'}: ${new Date().toLocaleString(language === 'fr' ? 'fr-FR' : 'de-DE')}`,
        marginX,
        y
      )

      y += 24
      filledEntries.forEach(([label, value]) => {
        if (y > pageHeight - 80) {
          doc.addPage()
          y = 56
        }

        doc.setFont('helvetica', 'bold')
        doc.setFontSize(11)
        const labelLines = doc.splitTextToSize(label, maxTextWidth)
        doc.text(labelLines, marginX, y)
        y += labelLines.length * 14

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(10)
        const valueLines = doc.splitTextToSize(value, maxTextWidth)
        doc.text(valueLines, marginX, y)
        y += valueLines.length * 14 + 10
      })

      const fileSuffix = new Date().toISOString().slice(0, 10)
      doc.save(`website-anfrage-${fileSuffix}.pdf`)
      setHasDownloadedPdf(true)
    } catch (error) {
      console.error('PDF generation error:', error)
      alert(t.downloadError || 'PDF konnte nicht erstellt werden. Bitte erneut versuchen.')
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <div className="form-container">
      <div className="form-container__wrapper">
        <form onSubmit={(e) => e.preventDefault()} className="form-container__form">
          <section className="form-container__intro" aria-label={isFrench ? 'Introduction du formulaire' : 'Formular-Einleitung'}>
            <div className="form-container__intro-icon" aria-hidden="true">💡</div>
            <h2 className="form-container__intro-title">{introCopy.title}</h2>
            <p className="form-container__intro-text">{introCopy.text}</p>
          </section>

          <section
            className="form-container__workflow-notice"
            aria-label={isFrench ? 'Procedure denvoi' : 'Ablaufhinweis'}
          >
            <h3>{t.workflowNoticeTitle || 'Wichtiger Hinweis zum Versand'}</h3>
            <p>
              {t.workflowNoticeText || 'Dieses Formular wird nicht direkt online versendet. Laden Sie es als PDF herunter und senden Sie die Datei anschliessend per E-Mail an'}{' '}
              <a href="mailto:digitalevit@gmail.com">digitalevit@gmail.com</a>.
            </p>
          </section>

          {/* Section 1: Contact */}
          <ContactSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 2: Project Type */}
          <ProjectTypeSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 3: Organization / Business Context */}
          <OrganizationSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 4: Content & Scope */}
          <ContentSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 5: Design & Branding */}
          <DesignSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 6: Features */}
          <FeaturesSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 7: Technology & CMS */}
          <TechSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 8: E-Commerce */}
          <ShopSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 9: Compliance & Security */}
          <ComplianceSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 10: SEO & Marketing */}
          <SEOSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 11: Performance & Hosting */}
          <PerformanceSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 12: Accessibility */}
          <AccessibilitySection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 13: Migration */}
          <MigrationSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 14: Support & Maintenance */}
          <SupportSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 15: Timeline & Budget */}
          <TimelineBudgetSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 16: Priorities & Decision Criteria */}
          <PrioritiesSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 17: Stakeholders & Internal Process */}
          <StakeholdersSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 18: Additional Information */}
          <AdditionalSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* Section 19: Files & Attachments */}
          <FilesSection 
            formData={formData}
            updateFormData={updateFormData}
            language={language}
          />

          {/* PDF Download + Manual Email */}
          <div className="form-container__submit">
            <button 
              type="button"
              className="form-container__submit-button"
              disabled={isGeneratingPdf}
              onClick={handleDownloadPdf}
            >
              {isGeneratingPdf
                ? t.downloadGenerating || 'PDF wird erstellt...'
                : t.downloadPdfButton || 'Formular als PDF herunterladen'}
            </button>

            {hasDownloadedPdf && (
              <div className="form-container__download-guide" role="status" aria-live="polite">
                <h3>{t.downloadGuideTitle || 'Naechster Schritt'}</h3>
                <p>
                  {t.downloadGuideText || 'Bitte senden Sie die heruntergeladene PDF-Datei per E-Mail an:'}{' '}
                  <a href="mailto:digitalevit@gmail.com">digitalevit@gmail.com</a>
                </p>
                <ol>
                  <li>{t.downloadGuideStep1 || 'PDF in Ihrem Download-Ordner oeffnen.'}</li>
                  <li>{t.downloadGuideStep2 || 'Neue E-Mail an digitalevit@gmail.com erstellen.'}</li>
                  <li>{t.downloadGuideStep3 || 'PDF-Datei anhaengen und E-Mail absenden.'}</li>
                </ol>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default FormContainer
